import { useState } from "react";
import { Star, RotateCcw, SlidersHorizontal } from "lucide-react";
import { FILTERS } from "../lib/filters";

// ─── category taxonomy ────────────────────────────────────────────────────────

const CATEGORIES = [
  { id: "all",      label: "All" },
  { id: "fav",      label: "Saved" },
  { id: "portrait", label: "Portrait" },
  { id: "film",     label: "Film" },
  { id: "bw",       label: "B&W" },
  { id: "food",     label: "Food" },
  { id: "pro",      label: "Pro" },
];

const FILTER_CAT = {
  "none":            "all",
  "clean-pro":       "portrait",
  "portrait-glow":   "portrait",
  "soft-skin":       "portrait",
  "linkedin-studio": "portrait",
  "cinematic-gold":  "film",
  "teal-orange":     "film",
  "vintage-film":    "film",
  "matte-film":      "film",
  "high-contrast":   "film",
  "cool-tone":       "film",
  "warm-tone":       "film",
  "bw-classic":      "bw",
  "food-warm":       "food",
  "travel-vivid":    "food",
  "night-bright":    "pro",
  "product-white":   "pro",
  "document-sharp":  "pro",
  "custom":          "all",
};

// ─── fine-tune params ─────────────────────────────────────────────────────────

const TUNE_PARAMS = [
  { id: "brightness",  label: "Bright",   min: -50,  max: 50 },
  { id: "contrast",    label: "Contrast", min: -50,  max: 50 },
  { id: "saturation",  label: "Sat",      min: -100, max: 100 },
  { id: "temperature", label: "Warm",     min: -50,  max: 50 },
];

// ─── component ────────────────────────────────────────────────────────────────

export default function FilterCarousel({
  selected,
  onSelect,
  strength,
  onStrength,
  favorites = [],
  onToggleFavorite,
  adjust = {},
  onAdjust,
}) {
  const [category, setCategory] = useState("all");
  const [showTune, setShowTune] = useState(false);

  const activeFilter = FILTERS.find((f) => f.id === selected) || FILTERS[0];

  const isFav = (id) =>
    favorites.includes(id) || (FILTERS.find((f) => f.id === id)?.favorite ?? false);

  const favCount = FILTERS.filter((f) => isFav(f.id)).length;

  const visible = FILTERS.filter((f) => {
    if (category === "fav") return isFav(f.id);
    if (category === "all") return true;
    return FILTER_CAT[f.id] === category;
  });
  const sorted = [...visible].sort((a, b) => Number(isFav(b.id)) - Number(isFav(a.id)));

  const handleSelect = (id) => {
    onSelect(id);
    // Reset fine-tune when changing filters
    if (id !== selected) onAdjust?.({});
  };

  const hasAdjust = Object.keys(adjust).length > 0;

  return (
    <div className="filter-dock-v2">

      {/* ── Top row: active name pill + tune + reset ── */}
      <div className="fdk-toprow">
        <div className="fdk-active-pill">
          <span className={`fdk-active-swatch preview-${selected}`} aria-hidden="true" />
          <strong>{activeFilter.name}</strong>
          {selected !== "none" && (
            <span className="fdk-active-strength">{strength}%</span>
          )}
          {hasAdjust && <span className="fdk-tuned-badge">Tuned</span>}
        </div>
        <div className="fdk-actions">
          {onAdjust && selected !== "none" && (
            <button
              type="button"
              className={`fdk-btn ${showTune ? "is-active" : ""}`}
              onClick={() => setShowTune((v) => !v)}
              aria-pressed={showTune}
              title="Fine-tune filter"
            >
              <SlidersHorizontal size={12} />
              <span>Tune</span>
            </button>
          )}
          <button
            type="button"
            className="fdk-btn fdk-reset-btn"
            onClick={() => {
              handleSelect("none");
              setShowTune(false);
            }}
            title="Remove filter"
          >
            <RotateCcw size={12} />
          </button>
        </div>
      </div>

      {/* ── Intensity slider ── */}
      <div className="fdk-strength-row">
        <span className="fdk-strength-label">Intensity</span>
        <div className="fdk-slider-wrap">
          <div className="fdk-slider-track">
            <div className="fdk-slider-fill" style={{ width: `${strength}%` }} />
          </div>
          <input
            type="range"
            min="0"
            max="100"
            value={strength}
            onChange={(e) => onStrength(Number(e.target.value))}
            className="fdk-slider-input"
            aria-label="Filter intensity"
          />
        </div>
        <span className="fdk-strength-val">{strength}%</span>
      </div>

      {/* ── Category tabs ── */}
      <div className="fdk-cats" role="tablist" aria-label="Filter categories">
        {CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            type="button"
            role="tab"
            aria-selected={category === cat.id}
            className={`fdk-cat ${category === cat.id ? "is-active" : ""}`}
            onClick={() => setCategory(cat.id)}
          >
            {cat.label}
            {cat.id === "fav" && favCount > 0 && (
              <span className="fdk-cat-badge">{favCount}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Filter chip strip ── */}
      <div className="fdk-strip" role="listbox" aria-label="Filters">
        {sorted.length === 0 ? (
          <p className="fdk-empty">
            No saved filters yet. Tap ★ on any filter to add it here.
          </p>
        ) : (
          sorted.map((filter) => {
            const isActive = selected === filter.id;
            const fav = isFav(filter.id);
            return (
              <button
                key={filter.id}
                type="button"
                className={`fdk-chip ${isActive ? "is-active" : ""}`}
                onClick={() => handleSelect(filter.id)}
                role="option"
                aria-selected={isActive}
              >
                <div className="fdk-chip-frame">
                  <span className={`fdk-chip-swatch preview-${filter.id}`} aria-hidden="true" />
                  {isActive && (
                    <div className="fdk-chip-active-ring" aria-hidden="true" />
                  )}
                  {isActive && (
                    <span className="fdk-chip-check" aria-hidden="true">✓</span>
                  )}
                  {onToggleFavorite && (
                    <button
                      type="button"
                      className={`fdk-chip-star ${fav ? "is-fav" : ""}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleFavorite(filter.id);
                      }}
                      aria-label={fav ? "Remove from saved" : "Save filter"}
                    >
                      <Star size={9} fill={fav ? "currentColor" : "none"} />
                    </button>
                  )}
                </div>
                <span className="fdk-chip-name">{filter.name}</span>
              </button>
            );
          })
        )}
      </div>

      {/* ── Fine-tune drawer ── */}
      {showTune && onAdjust && selected !== "none" && (
        <div className="fdk-tune">
          <div className="fdk-tune-header">
            <span>
              Fine Tune · <strong>{activeFilter.name}</strong>
            </span>
            <button
              type="button"
              className="fdk-tune-reset"
              onClick={() => onAdjust({})}
            >
              Reset
            </button>
          </div>
          <div className="fdk-tune-grid">
            {TUNE_PARAMS.map((p) => {
              const base = activeFilter.settings?.[p.id] ?? 0;
              const val = adjust[p.id] !== undefined ? adjust[p.id] : base;
              return (
                <div key={p.id} className="fdk-tune-row">
                  <div className="fdk-tune-labels">
                    <span>{p.label}</span>
                    <strong>{Math.round(val) > 0 ? `+${Math.round(val)}` : Math.round(val)}</strong>
                  </div>
                  <input
                    type="range"
                    min={p.min}
                    max={p.max}
                    value={val}
                    onChange={(e) =>
                      onAdjust({ ...adjust, [p.id]: Number(e.target.value) })
                    }
                    aria-label={p.label}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
